import {useState} from 'react';
import styled from 'styled-components';
import {useTicketSearch} from '../hooks/useTicketSearch';
import {useCreateTicketLink} from '../hooks/useCreateTicketLink';
import {useProjectSearch} from '../hooks/useProjectSearch';
import {TicketStatusBadge} from './TicketStatusBadge';
import type {TicketStatus} from '../types/ticket';

interface AddLinkModalProps {
	ticketId: string;
	existingLinkTargetIds: string[];
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

type LinkType = 'related' | 'child' | 'project';

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Backdrop = styled.div`
	position: absolute;
	inset: 0;
	background: rgb(0 0 0 / 0.4);
`;

const Dialog = styled.div`
	position: relative;
	z-index: 10;
	width: 100%;
	max-width: 28rem;
	border-radius: 12px;
	background: #ffffff;
	padding: 24px;
	box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
`;

const Title = styled.h2`
	margin: 0 0 16px;
	font-size: 18px;
	line-height: 28px;
	font-weight: 500;
	color: #0f172a;
`;

const TypeToggle = styled.div`
	margin-bottom: 16px;
	display: flex;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	padding: 4px;
`;

const TypeButton = styled.button<{$active: boolean}>`
	flex: 1 1 0%;
	border: 0;
	border-radius: 6px;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	cursor: pointer;
	transition: background-color 0.15s;
	background: ${p => (p.$active ? '#4f47d6' : 'transparent')};
	color: ${p => (p.$active ? '#ffffff' : '#475569')};
	&:hover {
		background: ${p => (p.$active ? '#4f47d6' : '#f1f5f9')};
	}
`;

const SearchRow = styled.div`
	margin-bottom: 12px;
`;

const SearchInput = styled.input`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 8px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const ResultsBox = styled.div`
	margin-bottom: 16px;
	max-height: 240px;
	overflow-y: auto;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
`;

const EmptyMessage = styled.div`
	padding: 24px 16px;
	text-align: center;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #64748b;
`;

const ResultList = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	> li + li {
		border-top: 1px solid #f1f5f9;
	}
`;

const ResultButton = styled.button<{$selected: boolean}>`
	display: flex;
	width: 100%;
	align-items: center;
	gap: 12px;
	border: 0;
	padding: 10px 16px;
	text-align: left;
	font: ${p => p.theme.ticketing.font.subheading2};
	cursor: pointer;
	transition: background-color 0.15s;
	background: ${p => (p.$selected ? '#eff6ff' : 'transparent')};
	box-shadow: ${p => (p.$selected ? 'inset 0 0 0 1px #bfdbfe' : 'none')};
	&:hover {
		background: ${p => (p.$selected ? '#eff6ff' : '#f8fafc')};
	}
	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;

const ResultNumber = styled.span`
	flex-shrink: 0;
	font-weight: 500;
	color: #64748b;
`;

const ResultColorDot = styled.span<{$color: string}>`
	flex-shrink: 0;
	height: 10px;
	width: 10px;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: ${p => p.$color};
`;

const ResultSubject = styled.span`
	min-width: 0;
	flex: 1 1 0%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: #0f172a;
`;

const ErrorText = styled.p`
	margin: 0 0 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #dc2626;
`;

const Actions = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

const CancelButton = styled.button`
	border: 0;
	border-radius: 4px;
	background: transparent;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #475569;
	cursor: pointer;
	&:hover {
		background: #f1f5f9;
	}
`;

const SubmitButton = styled.button`
	border: 0;
	border-radius: 4px;
	background: #4f47d6;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	&:hover {
		background: #3d36b3;
	}
	&:disabled {
		opacity: 0.5;
	}
`;

const TYPE_OPTIONS: {value: LinkType; label: string}[] = [
	{value: 'related', label: 'Related ticket'},
	{value: 'child', label: 'Child ticket'},
	{value: 'project', label: 'Project'},
];

export function AddLinkModal({ticketId, existingLinkTargetIds, open, onClose, onSuccess}: AddLinkModalProps) {
	const [linkType, setLinkType] = useState<LinkType>('related');
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const {results: ticketResults, loading: searchingTickets} = useTicketSearch(linkType !== 'project' ? searchQuery : '');
	const {results: projectResults, loading: searchingProjects} = useProjectSearch(linkType === 'project' ? searchQuery : '');
	const {submit, loading: submitting, error} = useCreateTicketLink();

	if (!open) return null;

	const searching = linkType === 'project' ? searchingProjects : searchingTickets;
	const filteredResults = ticketResults.filter(r => r.id !== ticketId);

	const placeholder = linkType === 'project' ? 'Search projects...' : 'Search tickets...';

	function handleTypeChange(type: LinkType) {
		setLinkType(type);
		setSearchQuery('');
		setSelectedId(null);
	}

	async function handleSubmit() {
		if (!selectedId) return;

		const input =
			linkType === 'project'
				? {ticketId, linkType, targetProjectId: selectedId}
				: {ticketId, linkType, targetTicketId: selectedId};

		try {
			await submit(input);
			onClose();
			onSuccess?.();
		} catch {
			// error state is managed by useCreateTicketLink
		}
	}

	return (
		<Overlay>
			<Backdrop data-testid="add-link-backdrop" onClick={onClose} />
			<Dialog>
				<Title>Add link</Title>

				<TypeToggle>
					{TYPE_OPTIONS.map(opt => (
						<TypeButton
							key={opt.value}
							type="button"
							$active={linkType === opt.value}
							onClick={() => handleTypeChange(opt.value)}
						>
							{opt.label}
						</TypeButton>
					))}
				</TypeToggle>

				<SearchRow>
					<SearchInput
						type="text"
						placeholder={placeholder}
						value={searchQuery}
						onChange={e => {
							setSearchQuery(e.target.value);
							setSelectedId(null);
						}}
					/>
				</SearchRow>

				<ResultsBox>
					{searching ? (
						<EmptyMessage>Searching...</EmptyMessage>
					) : searchQuery.length === 0 ? (
						<EmptyMessage>
							{linkType === 'project' ? 'Type to search for projects.' : 'Type to search for tickets.'}
						</EmptyMessage>
					) : linkType === 'project' ? (
						projectResults.length === 0 ? (
							<EmptyMessage>No results found.</EmptyMessage>
						) : (
							<ResultList>
								{projectResults.map(project => {
									const isLinked = existingLinkTargetIds.includes(project.id);
									const isSelected = selectedId === project.id;
									return (
										<li key={project.id}>
											<ResultButton
												type="button"
												disabled={isLinked}
												$selected={isSelected}
												onClick={() => setSelectedId(project.id)}
											>
												<ResultColorDot $color={project.color ?? '#94a3b8'} aria-hidden="true" />
												<ResultSubject>{project.name}</ResultSubject>
											</ResultButton>
										</li>
									);
								})}
							</ResultList>
						)
					) : filteredResults.length === 0 ? (
						<EmptyMessage>No results found.</EmptyMessage>
					) : (
						<ResultList>
							{filteredResults.map(result => {
								const isLinked = existingLinkTargetIds.includes(result.id);
								const isSelected = selectedId === result.id;
								return (
									<li key={result.id}>
										<ResultButton
											type="button"
											disabled={isLinked}
											$selected={isSelected}
											onClick={() => setSelectedId(result.id)}
										>
											<ResultNumber>TK-{result.number}</ResultNumber>
											<ResultSubject>{result.subject}</ResultSubject>
											<TicketStatusBadge status={result.status as TicketStatus} />
										</ResultButton>
									</li>
								);
							})}
						</ResultList>
					)}
				</ResultsBox>

				{error && <ErrorText>{error}</ErrorText>}

				<Actions>
					<CancelButton type="button" onClick={onClose}>
						Cancel
					</CancelButton>
					<SubmitButton type="button" disabled={!selectedId || submitting} onClick={handleSubmit}>
						Add link
					</SubmitButton>
				</Actions>
			</Dialog>
		</Overlay>
	);
}
